import React, { useState, Suspense } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import categories from '../products';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Html, useProgress } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import placeholderImg from '../images/logo_jm_simpel.png';

function Loader() {
  const { progress } = useProgress();
  return <Html center>{progress.toFixed(0)} % geladen</Html>;
}

function StlModel({ url, color }) {
  const geometry = useLoader(STLLoader, url);
  // Center and fit geometry
  geometry.computeBoundingBox();
  const boundingBox = geometry.boundingBox;
  const center = [
    (boundingBox.max.x + boundingBox.min.x) / 2,
    (boundingBox.max.y + boundingBox.min.y) / 2,
    (boundingBox.max.z + boundingBox.min.z) / 2,
  ];
  const size = [
    boundingBox.max.x - boundingBox.min.x,
    boundingBox.max.y - boundingBox.min.y,
    boundingBox.max.z - boundingBox.min.z,
  ];
  const maxDim = Math.max(...size);
  // We willen het model laten passen in een virtuele box van 2x2x2
  const fitScale = 2 / maxDim;
  return (
    <mesh geometry={geometry} scale={fitScale} position={[-center[0], -center[1], -center[2]]}>
      <meshStandardMaterial color={color} metalness={0.25} roughness={0.75} />
    </mesh>
  );
}

const colorMap = {
  wit: '#ffffff',
  grijs: '#888888',
  steenrood: '#b55239',
  bruin: '#8b5c2a',
  zwart: '#222222',
  blauw: '#4682b4',
};

function StlViewer({ url }) {
  const geometry = useLoader(STLLoader, url);
  geometry.computeBoundingBox();
  const boundingBox = geometry.boundingBox;
  const center = [
    (boundingBox.max.x + boundingBox.min.x) / 2,
    (boundingBox.max.y + boundingBox.min.y) / 2,
    (boundingBox.max.z + boundingBox.min.z) / 2,
  ];
  const size = [
    boundingBox.max.x - boundingBox.min.x,
    boundingBox.max.y - boundingBox.min.y,
    boundingBox.max.z - boundingBox.min.z,
  ];
  const maxDim = Math.max(...size);
  const fitScale = 2 / maxDim;
  return (
    <Canvas orthographic camera={{ position: [0, 0, 10], zoom: 100 }} style={{ background: 'var(--color-grey)', borderRadius: '1rem', height: 300 }}>
      <ambientLight intensity={0.8} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <mesh geometry={geometry} scale={fitScale} position={[-center[0], -center[1], -center[2]]}>
        <meshStandardMaterial color={'#3DDAD7'} metalness={0.25} roughness={0.75} />
      </mesh>
      <OrbitControls makeDefault />
    </Canvas>
  );
}

export default function ProductDetail() {
  const { catId, productIndex } = useParams();
  const category = categories.find(cat => cat.id === catId);
  const product = category?.products[productIndex];
  const [showColor, setShowColor] = useState(null);
  const [selectedImg, setSelectedImg] = useState(0);
  const plaColors = [
    { name: 'almont yellow', file: require('../PLA/almont yellow.jpg') },
    { name: 'dark grey', file: require('../PLA/dark grey.jpg') },
    { name: 'peach pink', file: require('../PLA/peach pink.jpg') },
    { name: 'mint green', file: require('../PLA/mint green.jpg') },
    { name: 'lake blue', file: require('../PLA/lake blue.jpg') },
    { name: 'milky white', file: require('../PLA/milky white.jpg') },
    { name: 'deep black', file: require('../PLA/deep black.jpg') },
  ];

  const images = product && product.images && product.images.length > 0 ? product.images : [placeholderImg];

  if (!product) return <div className="container-custom py-16">Voorbeeld niet gevonden.</div>;

  return (
    <div className="min-h-screen bg-gray-50" style={{ background: 'var(--color-bg)', color: 'var(--color-text)' }}>
      <div className="container-custom section">
        <div className="card max-w-full md:max-w-3xl mx-auto flex flex-col md:flex-row gap-6 md:gap-8 p-4 sm:p-8">
          <div className="flex-1 flex flex-col items-center justify-center">
            {/* Thumbnails */}
            <div className="flex flex-row md:flex-col gap-2 mb-2 md:mb-0 md:mr-4">
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={product.name + ' ' + (idx + 1)}
                  className={`w-12 h-12 sm:w-14 sm:h-14 object-cover rounded shadow border border-grey cursor-pointer ${selectedImg === idx ? 'ring-2 ring-accent1' : ''}`}
                  onClick={() => setSelectedImg(idx)}
                  style={{ opacity: selectedImg === idx ? 1 : 0.7 }}
                />
              ))}
            </div>
            {/* Hoofdafbeelding */}
            <img src={images[selectedImg]} alt={product.name} className="w-48 h-48 sm:w-64 sm:h-64 object-contain mb-4 rounded-xl shadow" style={{ borderRadius: '1rem' }} />
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-xl sm:text-3xl font-bold mb-2 text-accent1">{product.name}</h1>
            <p className="mb-4 text-base sm:text-lg">{product.description}</p>
            <div className="mb-4 text-sm text-gray-500">Afmetingen: {product.dimensions}</div>
            <Link to={product.contactLink} className="btn-primary px-4 sm:px-6 py-2 sm:py-3 text-base sm:text-lg mt-4 w-full text-center">Vraag aan</Link>

            {/* PLA kleuren */}
            <div className="mt-8">
              <h3 className="text-base sm:text-lg font-bold mb-2 text-accent1">Beschikbare PLA kleuren</h3>
              <div className="flex flex-wrap gap-3 sm:gap-4 items-center mb-2">
                {plaColors.map((color, idx) => (
                  <div key={color.name} className="flex flex-col items-center cursor-pointer" onClick={() => setShowColor(idx)}>
                    <img src={color.file} alt={color.name} className="w-8 h-8 sm:w-12 sm:h-12 object-cover rounded shadow border border-grey hover:scale-110 transition" />
                    <span className="text-xs mt-1 text-gray-700">{color.name}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs sm:text-sm text-gray-500 mt-2">Andere kleuren zijn mogelijk op aanvraag.</div>
            </div>

            {/* Lightbox/modal voor grote kleurweergave */}
            {showColor !== null && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={() => setShowColor(null)}>
                <div className="bg-white rounded-xl p-4 shadow-lg max-w-xs flex flex-col items-center">
                  <img src={plaColors[showColor].file} alt={plaColors[showColor].name} className="w-48 h-48 sm:w-64 sm:h-64 object-contain rounded mb-2" />
                  <div className="text-base font-semibold text-accent1 mb-2">{plaColors[showColor].name}</div>
                  <button className="btn-secondary mt-2" onClick={() => setShowColor(null)}>Sluiten</button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="container-custom mt-8 text-center">
          <Link to={`/categorie/${catId}`} className="text-accent1 hover:underline">← Terug naar {category?.name}</Link>
        </div>
      </div>
    </div>
  );
} 